import { Button } from '@/components/ui/button';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { cn } from '@/lib/utils';

import { zodResolver } from '@hookform/resolvers/zod';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import useFormattedDate from '@/hooks/useFormattedDate';
import formBookingRangePickerSchema from '@/schemas/form-choose-service-division-group';
import { useRootStore } from '@/stores/root-store';
import { getTimeZone } from '@/utils/dates';
import { isSameDay, startOfDay } from 'date-fns';
import { z } from 'zod';
import { ChooseDivisionStepProps } from './ChooseServiceDivisionGroup.types';

const ChooseDivisionStep: React.FC<ChooseDivisionStepProps> = ({
  serviceDivisionGroups,
  organization: { tz }
}) => {
  const { t } = useTranslation();
  const addStep = useRootStore((store) => store.addStep);
  const removeStep = useRootStore((store) => store.removeStep);
  const currentStep = useRootStore((store) => store.currentStep);
  const loadStepServicesData = useRootStore(
    (store) => store.loadStepServicesData
  );
  const form = useForm<z.infer<typeof formBookingRangePickerSchema>>({
    resolver: zodResolver(formBookingRangePickerSchema)
  });
  const { formattedDate } = useFormattedDate(tz);

  const selectedRanges = form.watch('serviceDivisionGroups');

  function onSubmit(data: z.infer<typeof formBookingRangePickerSchema>) {
    const divisions = serviceDivisionGroups?.filter((division) =>
      data.serviceDivisionGroups.includes(division.id)
    );
    loadStepServicesData({
      serviceDivisionGroups: divisions
    });
    addStep(currentStep);
  }

  const rangeDivisions = [...(serviceDivisionGroups ?? [])].sort((a, b) => a.order - b.order);

  const handleRangeSelected = (rangeId: string) => {
    const isAlreadySelected = selectedRanges?.includes(rangeId);
    const updatedSelection = isAlreadySelected
      ? selectedRanges?.filter((id) => id !== rangeId)
      : [...(selectedRanges ?? []), rangeId];
    form.setValue('serviceDivisionGroups', updatedSelection);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="serviceDivisionGroups"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>{t('newBooking.divisionGroups.title')}</FormLabel>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {rangeDivisions?.map((range) => {
                  const startTime = getTimeZone(range.startTime, tz);
                  const endTime = getTimeZone(range.endTime, tz);

                  const sameDay = isSameDay(
                    startOfDay(startTime),
                    startOfDay(endTime)
                  );
                  return (
                    <div
                      key={range.id}
                      className={cn(
                        'p-4 border rounded-md cursor-pointer transition-colors',
                        !range.active
                          ? 'bg-gray-100 opacity-60 cursor-not-allowed'
                          : selectedRanges?.includes(range.id)
                            ? 'border-blue-500 bg-blue-50'
                            : 'hover:border-blue-200 hover:bg-blue-50'
                      )}
                      onClick={() =>
                        range.active && handleRangeSelected(range.id)
                      }
                    >
                      <h4 className="font-medium text-lg">{range.name}</h4>
                      <p className="text-gray-600">{range.description}</p>
                      <p className="text-gray-600 text-sm">
                        {sameDay ? (
                          <>
                            {formattedDate({
                              date: range.startTime,
                              dateFormat: 'date'
                            })}
                          </>
                        ) : (
                          <>
                            {formattedDate({
                              date: range.startTime,
                              dateFormat: 'date'
                            })}{' '}
                            -{' '}
                            {formattedDate({
                              date: range.endTime,
                              dateFormat: 'date'
                            })}
                          </>
                        )}
                      </p>
                      <p className="text-gray-600 text-sm">
                        {t('newBooking.divisionGroups.from')}{' '}
                        {range.startHour || range.startTime}{' '}
                        {t('newBooking.divisionGroups.to')}{' '}
                        {(() => {
                          const startHour = range.startHour || range.startTime;
                          if (typeof startHour === 'string' && range.duration) {
                            // Parse the start hour (assuming HH:MM format)
                            const [hours, minutes] = startHour
                              .split(':')
                              .map(Number);
                            const startMinutes = hours * 60 + (minutes || 0);
                            const endMinutes = startMinutes + range.duration;
                            const endHours = Math.floor(endMinutes / 60);
                            const endMins = endMinutes % 60;
                            return `${endHours
                              .toString()
                              .padStart(2, '0')}:${endMins
                              .toString()
                              .padStart(2, '0')}`;
                          }
                          return range.startHour || range.startTime;
                        })()}{' '}
                        hs
                      </p>
                      <span
                        className={cn(
                          'inline-block mt-2 px-2 py-1 text-xs rounded-full',
                          range.active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        )}
                      >
                        {range.active
                          ? t('newBooking.divisionGroups.available')
                          : t('newBooking.divisionGroups.unavailable')}
                      </span>
                    </div>
                  );
                })}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex flex-col gap-3">
          <Button type="submit">{t('newBooking.continue')}</Button>
          <Button
            onClick={() => {
              removeStep(currentStep);
              loadStepServicesData({ serviceDivisionGroups: undefined });
            }}
            variant={'outline'}
          >
            {t('newBooking.goBack')}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default ChooseDivisionStep;
